import { useState, useEffect, useRef } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, X, Loader2 } from "lucide-react";

const BarcodeScanner = ({ isOpen, onClose, onBarcodeScanned }) => {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const scannerRef = useRef(null);
  const html5QrcodeRef = useRef(null);

  useEffect(() => {
    if (isOpen && !scanning) {
      startScanning();
    }

    return () => {
      stopScanning();
    };
  }, [isOpen]);

  const startScanning = async () => {
    try {
      setError(null);
      setScanning(true);

      html5QrcodeRef.current = new Html5Qrcode("barcode-reader");

      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
      };

      await html5QrcodeRef.current.start(
        { facingMode: "environment" },
        config,
        onScanSuccess,
        onScanFailure
      );
    } catch (err) {
      console.error("Error starting scanner:", err);
      setError("Unable to access camera. Please ensure camera permissions are granted.");
      setScanning(false);
    }
  };

  const stopScanning = async () => {
    if (html5QrcodeRef.current && scanning) {
      try {
        await html5QrcodeRef.current.stop();
        html5QrcodeRef.current.clear();
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
      setScanning(false);
    }
  };

  const onScanSuccess = (decodedText) => {
    console.log("Barcode scanned:", decodedText);
    stopScanning();
    onBarcodeScanned(decodedText);
    onClose();
  };

  const onScanFailure = (error) => {
    // Silent - scanning failures are normal when no barcode is in view
  };

  const handleClose = () => {
    stopScanning();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]" data-testid="barcode-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#3b444d] flex items-center gap-2">
            <Camera className="w-6 h-6" />
            Scan Product Barcode
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {error ? (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="scanner-error">
              <p className="text-red-700">{error}</p>
              <Button
                onClick={startScanning}
                className="mt-4 bg-[#e83b84] hover:bg-[#d02f73] text-white"
                data-testid="retry-scanner-button"
              >
                Try Again
              </Button>
            </div>
          ) : (
            <div className="relative">
              <div
                id="barcode-reader"
                className="rounded-lg overflow-hidden border-4 border-[#64c2c8]"
                style={{ minHeight: "300px" }}
                data-testid="barcode-reader-view"
              ></div>
              
              {scanning && (
                <div className="mt-4 flex items-center justify-center gap-2 text-gray-600">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Position barcode within the frame...</span>
                </div>
              )}

              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-gray-700">
                  <strong className="text-[#3b444d]">Tips:</strong>
                </p>
                <ul className="text-sm text-gray-600 mt-2 space-y-1 ml-4 list-disc">
                  <li>Hold the product barcode steady within the frame</li>
                  <li>Ensure good lighting for best results</li>
                  <li>Works with EAN, UPC, and other standard barcodes</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button
            variant="outline"
            onClick={handleClose}
            className="border-2 border-gray-300"
            data-testid="close-scanner-button"
          >
            <X className="w-4 h-4 mr-2" />
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BarcodeScanner;
